import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Alert, Space, Switch, Tooltip, Typography } from 'antd';
import { HolderOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MenuTemplate from './MenuTemplate';
import {
  useUpdateMenuVisibilityMutation,
  useUpdateMenuOrderMutation,
} from '../../services/menuDigitalApi';
import type {
  MenuEditor,
  MenuEditorCategory,
  MenuEditorProduct,
} from '../../types/menuDigital';
import '../../styles/publicMenu.css';
import '../../styles/menuEditor.css';

const { Text } = Typography;

// Editor en vivo: la MISMA plantilla que ve el comensal, con una capa de
// controles encima. No es una "vista previa" que imita la página pública —
// es la página pública con overlay, que es lo único que garantiza que lo que
// el dueño aprueba sea lo que se publica.

interface MenuEditorCanvasProps {
  restaurantId: string;
  menu: MenuEditor;
}

/** Envoltura arrastrable de un renglón de producto. */
const SortableProduct: React.FC<{
  product: MenuEditorProduct;
  categoriaId: string;
  children: React.ReactNode;
  onToggle: (visible: boolean) => void;
}> = ({ product, categoriaId, children, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: product.id,
    // El tipo y la sección viajan con el elemento para que la detección de
    // colisión pueda filtrar: sin esto, arrastrar una sección "aterriza" sobre
    // un producto y el movimiento se descarta.
    data: { type: 'product', categoriaId },
  });

  return (
    <div
      ref={setNodeRef}
      className={`me-row${product.visible ? '' : ' me-row--hidden'}${isDragging ? ' me-row--dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        type="button"
        className="me-handle"
        aria-label={`Mover ${product.name}`}
        {...attributes}
        {...listeners}
      >
        <HolderOutlined />
      </button>
      <div className="me-row-body">{children}</div>
      <Tooltip title={product.visible ? 'Se muestra en tu menú' : 'Oculto en tu menú'}>
        <button
          type="button"
          className="me-eye"
          aria-label={product.visible ? `Ocultar ${product.name}` : `Mostrar ${product.name}`}
          aria-pressed={product.visible}
          onClick={() => onToggle(!product.visible)}
        >
          {product.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </button>
      </Tooltip>
    </div>
  );
};

/** Envoltura arrastrable de una categoría completa, con su propio ojo. */
const SortableCategory: React.FC<{
  categoria: MenuEditorCategory;
  children: React.ReactNode;
  onToggle: (visible: boolean) => void;
}> = ({ categoria, children, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: categoria.id,
    data: { type: 'category' },
  });

  return (
    <div
      ref={setNodeRef}
      className={`me-cat${categoria.visible ? '' : ' me-cat--hidden'}${isDragging ? ' me-cat--dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {/* Barra de control de la sección, en el flujo y no flotando sobre el
          margen: antes el asa colgaba a la izquierda con margen negativo,
          dentro de un contenedor con overflow-x oculto, así que quedaba pegada
          al borde o recortada — imposible de agarrar. Además, mostrar aquí el
          nombre deja claro qué sección se está moviendo. */}
      <div className="me-cat-bar">
        <button
          type="button"
          className="me-handle"
          aria-label={`Mover la sección ${categoria.name}`}
          {...attributes}
          {...listeners}
        >
          <HolderOutlined />
        </button>
        <span className="me-cat-nombre">{categoria.name}</span>
        <Tooltip title={categoria.visible ? 'Sección visible en tu menú' : 'Sección oculta en tu menú'}>
          <button
            type="button"
            className="me-eye"
            aria-label={
              categoria.visible ? `Ocultar la sección ${categoria.name}` : `Mostrar la sección ${categoria.name}`
            }
            aria-pressed={categoria.visible}
            onClick={() => onToggle(!categoria.visible)}
          >
            {categoria.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          </button>
        </Tooltip>
      </div>
      <div className="me-cat-body">{children}</div>
    </div>
  );
};

const MenuEditorCanvas: React.FC<MenuEditorCanvasProps> = ({ restaurantId, menu }) => {
  const { message } = App.useApp();
  const [updateVisibility] = useUpdateMenuVisibilityMutation();
  const [updateOrder] = useUpdateMenuOrderMutation();

  // Copia local para que el arrastre y los clics se sientan inmediatos; el
  // servidor confirma después.
  const [draft, setDraft] = useState<MenuEditor>(menu);

  const [showModifiers, setShowModifiers] = useState(false);

  const sensors = useSensors(
    // La distancia mínima evita que un clic en el ojo se interprete como
    // arrastre; el retardo táctil deja que el dedo haga scroll de la página
    // sin arrastrar sin querer, que es la queja clásica en teléfono.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Agrupa los cambios de visibilidad: marcar media carta a clics dispararía
  // una petición por clic.
  const pending = useRef<{
    categories: Map<string, boolean>;
    products: Map<string, boolean>;
    groups: Map<string, boolean>;
    variants: Map<string, boolean>;
  }>({
    categories: new Map(),
    products: new Map(),
    groups: new Map(),
    variants: new Map(),
  });
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(0);

  const hayCambiosSinConfirmar = () =>
    inFlight.current > 0 ||
    pending.current.categories.size > 0 ||
    pending.current.products.size > 0 ||
    pending.current.groups.size > 0 ||
    pending.current.variants.size > 0;

  // Resincronizar con el servidor SOLO cuando no hay nada en vuelo. Si otra
  // pantalla invalida el editor (agregar un producto desde la pestaña
  // Productos, por ejemplo) mientras el usuario sigue picando ojos, adoptar la
  // respuesta pisaría los clics que todavía no se han mandado y el interruptor
  // se vería rebotar.
  useEffect(() => {
    if (!hayCambiosSinConfirmar()) {
      setDraft(menu);
    }
  }, [menu]);

  const flush = useCallback(async () => {
    const { categories, products, groups, variants } = pending.current;
    if (categories.size === 0 && products.size === 0 && groups.size === 0 && variants.size === 0) return;

    const payload = {
      restaurantId,
      categories: Array.from(categories, ([id, visible]) => ({ id, visible })),
      products: Array.from(products, ([id, visible]) => ({ id, visible })),
      variantGroups: Array.from(groups, ([id, visible]) => ({ id, visible })),
      variants: Array.from(variants, ([id, visible]) => ({ id, visible })),
    };
    pending.current = {
      categories: new Map(),
      products: new Map(),
      groups: new Map(),
      variants: new Map(),
    };

    inFlight.current += 1;
    try {
      await updateVisibility(payload).unwrap();
    } catch {
      message.error('No se pudieron guardar los cambios. Recarga para ver cómo quedó tu menú.');
    } finally {
      inFlight.current -= 1;
    }
  }, [restaurantId, updateVisibility, message]);

  const queueVisibility = useCallback(
    (kind: 'categories' | 'products' | 'groups' | 'variants', id: string, visible: boolean) => {
      pending.current[kind].set(id, visible);
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(flush, 600);
    },
    [flush]
  );

  // Al desmontar, mandar lo que quedó en el aire: si no, salir de la pestaña
  // justo después de un clic perdería ese cambio.
  useEffect(() => () => { void flush(); }, [flush]);

  const toggleCategoria = (categoriaId: string, visible: boolean) => {
    setDraft((prev) => ({
      ...prev,
      categorias: prev.categorias.map((c) => (c.id === categoriaId ? { ...c, visible } : c)),
    }));
    queueVisibility('categories', categoriaId, visible);
  };

  const toggleProduct = (categoriaId: string, productId: string, visible: boolean) => {
    setDraft((prev) => ({
      ...prev,
      categorias: prev.categorias.map((c) =>
        c.id !== categoriaId
          ? c
          : { ...c, products: c.products.map((p) => (p.id === productId ? { ...p, visible } : p)) }
      ),
    }));
    queueVisibility('products', productId, visible);
  };

  const toggleGroup = (productId: string, groupId: string, visible: boolean) => {
    setDraft((prev) => ({
      ...prev,
      categorias: prev.categorias.map((c) => ({
        ...c,
        products: c.products.map((p) =>
          p.id !== productId
            ? p
            : {
                ...p,
                modificadores: p.modificadores.map((g) => (g.id === groupId ? { ...g, visible } : g)),
              }
        ),
      })),
    }));
    queueVisibility('groups', groupId, visible);
  };

  const toggleVariant = (productId: string, groupId: string, variantId: string, visible: boolean) => {
    setDraft((prev) => ({
      ...prev,
      categorias: prev.categorias.map((c) => ({
        ...c,
        products: c.products.map((p) =>
          p.id !== productId
            ? p
            : {
                ...p,
                modificadores: p.modificadores.map((g) =>
                  g.id !== groupId
                    ? g
                    : {
                        ...g,
                        options: g.options.map((o) => (o.id === variantId ? { ...o, visible } : o)),
                      }
                ),
              }
        ),
      })),
    }));
    queueVisibility('variants', variantId, visible);
  };

  // Las secciones y los productos son zonas de destino del MISMO DndContext, y
  // los productos son muchos más y están dentro de las secciones. Con la
  // detección por defecto, arrastrar una sección siempre "aterrizaba" sobre un
  // producto: el destino no era una sección, el movimiento no encajaba en
  // ninguna rama y se descartaba en silencio — por eso las secciones no se
  // podían mover y los productos sí. Aquí cada arrastre solo compite contra
  // destinos de su mismo tipo, y los productos además solo dentro de su
  // sección (cambiar un platillo de sección se hace en su formulario, no
  // arrastrando entre secciones).
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const tipo = args.active.data.current?.type;
    const categoriaId = args.active.data.current?.categoriaId;

    const candidatos = args.droppableContainers.filter((c) => {
      if (c.data.current?.type !== tipo) return false;
      if (tipo === 'product') return c.data.current?.categoriaId === categoriaId;
      return true;
    });

    return closestCenter({ ...args, droppableContainers: candidatos });
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const categoryIds = draft.categorias.map((c) => c.id);
    if (categoryIds.includes(activeId) && categoryIds.includes(overId)) {
      const next = arrayMove(
        draft.categorias,
        categoryIds.indexOf(activeId),
        categoryIds.indexOf(overId)
      );
      setDraft({ ...draft, categorias: next });
      try {
        await updateOrder({ restaurantId, categories: next.map((c) => c.id) }).unwrap();
      } catch {
        message.error('No se pudo guardar el orden.');
      }
      return;
    }

    // Producto: solo se reordena dentro de su propia categoría. Mover un
    // platillo a otra sección es cambiarle la categoría, y eso se hace en el
    // formulario del producto, no arrastrando.
    const categoria = draft.categorias.find((c) => c.products.some((p) => p.id === activeId));
    if (!categoria) return;
    const productIds = categoria.products.map((p) => p.id);
    if (!productIds.includes(overId)) return;

    const nextProducts = arrayMove(
      categoria.products,
      productIds.indexOf(activeId),
      productIds.indexOf(overId)
    );
    setDraft({
      ...draft,
      categorias: draft.categorias.map((c) =>
        c.id === categoria.id ? { ...c, products: nextProducts } : c
      ),
    });
    try {
      await updateOrder({
        restaurantId,
        products: [{ categoryId: categoria.id, productIds: nextProducts.map((p) => p.id) }],
      }).unwrap();
    } catch {
      message.error('No se pudo guardar el orden.');
    }
  };

  // El lienzo SIEMPRE muestra lo que se publica, con el mismo filtro que aplica
  // el backend (grupo visible, dentro solo variantes visibles, y fuera el grupo
  // que se quedó sin ninguna). Antes, con el modo modificadores encendido se
  // pasaban todos sin filtrar: apagar un grupo no cambiaba nada en la vista y
  // parecía que el interruptor no servía. Los apagados siguen siendo
  // alcanzables en el panel de edición de abajo, que lee del borrador completo.
  const menuParaPlantilla = useMemo(
    () => ({
      ...draft,
      categorias: draft.categorias.map((c) => ({
        ...c,
        products: c.products.map((p) => ({
          ...p,
          modificadores: p.modificadores
            .filter((g) => g.visible)
            .map((g) => ({ ...g, options: g.options.filter((o) => o.visible) }))
            .filter((g) => g.options.length > 0),
        })),
      })),
    }),
    [draft]
  );

  // El panel de edición NO puede leer del menú ya filtrado: ahí los grupos
  // apagados no existen y no habría desde dónde volver a encenderlos. Se busca
  // el producto completo en el borrador.
  const productosCompletos = useMemo(() => {
    const mapa = new Map<string, MenuEditorProduct>();
    draft.categorias.forEach((c) => c.products.forEach((p) => mapa.set(p.id, p)));
    return mapa;
  }, [draft]);

  const totalOcultos = draft.categorias.reduce(
    (n, c) => n + c.products.filter((p) => !p.visible).length,
    0
  );
  const conModificadores = draft.categorias.reduce(
    (n, c) => n + c.products.filter((p) => p.modificadores.length > 0).length,
    0
  );

  return (
    <div className="me-wrap">
      <div className="me-toolbar">
        <Space size={16} wrap>
          <Text type="secondary">
            Toca el ojo para mostrar u ocultar. Arrastra <HolderOutlined /> para cambiar el orden.
          </Text>
          {conModificadores > 0 && (
            <Space size={8}>
              <Switch size="small" checked={showModifiers} onChange={setShowModifiers} />
              <Text>Editar modificadores</Text>
            </Space>
          )}
        </Space>
        {totalOcultos > 0 && (
          <Text type="secondary">
            {totalOcultos} {totalOcultos === 1 ? 'producto oculto' : 'productos ocultos'}
          </Text>
        )}
      </div>

      {showModifiers && conModificadores > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Elige qué modificadores vale la pena publicar"
          description="Los que venden, como los sabores de unas alitas, enriquecen tu carta. Los operativos, como el término de la carne, la ensucian. Vienen apagados."
        />
      )}

      <div className="me-canvas">
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
          <SortableContext
            items={draft.categorias.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <MenuTemplate
              menu={menuParaPlantilla}
              showFooter={false}
              // Ids, no nombres: es una superficie de arrastre.
              categoryKey={(c) => (c as MenuEditorCategory).id}
              productKey={(p) => (p as MenuEditorProduct).id}
              renderCategoria={(categoria, node) => {
                const cat = categoria as MenuEditorCategory;
                return (
                  <SortableCategory
                    categoria={cat}
                    onToggle={(visible) => toggleCategoria(cat.id, visible)}
                  >
                    <SortableContext
                      items={cat.products.map((p) => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {node}
                    </SortableContext>
                  </SortableCategory>
                );
              }}
              renderProduct={(producto, categoria, node) => {
                const prod = producto as MenuEditorProduct;
                const cat = categoria as MenuEditorCategory;
                return (
                  <SortableProduct
                    product={prod}
                    categoriaId={cat.id}
                    onToggle={(visible) => toggleProduct(cat.id, prod.id, visible)}
                  >
                    {node}
                    {showModifiers && (productosCompletos.get(prod.id)?.modificadores.length ?? 0) > 0 && (
                      <div className="me-mods">
                        {productosCompletos.get(prod.id)!.modificadores.map((grupo) => (
                          <div key={grupo.id}>
                            <label className="me-mods-row">
                              <Switch
                                size="small"
                                checked={grupo.visible}
                                onChange={(checked) => toggleGroup(prod.id, grupo.id, checked)}
                              />
                              <span>{grupo.name}</span>
                            </label>
                            {/* Las opciones solo se listan con el grupo
                                encendido: apagar una variante de un grupo que
                                no se publica no cambia nada y solo llenaría de
                                controles muertos. */}
                            {grupo.visible && (
                              <div className="me-mods-vars">
                                {grupo.options.map((opcion) => (
                                  <label key={opcion.id} className="me-mods-row">
                                    <Switch
                                      size="small"
                                      checked={opcion.visible}
                                      onChange={(checked) =>
                                        toggleVariant(prod.id, grupo.id, opcion.id, checked)
                                      }
                                    />
                                    <span className={opcion.visible ? undefined : 'me-mods-hint'}>
                                      {opcion.name}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </SortableProduct>
                );
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default MenuEditorCanvas;
